if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.slideListenerBound = true;
        this.bindThumbnails(this.elements.thumbnails);
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      /* ONE DELEGATED LISTENER, NOT ONE PER THUMBNAIL — because the strip is
         now REPLACED on every colour change (see `replaceThumbnails`), and a
         listener bound to each button at construction time would leave every
         thumbnail that arrived later dead to clicks. `[data-target] button`
         and not bare `button`: the strip's own prev/next slider arrows are
         buttons too, and they sit outside any `[data-target]`. */
      bindThumbnails(thumbnails) {
        thumbnails.addEventListener('click', (event) => {
          const item = event.target.closest('[data-target]');
          if (!item || !event.target.closest('button') || !thumbnails.contains(item)) return;
          this.setActiveMedia(item.dataset.target, false);
        });
      }

      /* ⚠ THE THUMBNAIL STRIP HAS TO FOLLOW THE COLOUR, OR BUYING BREAKS.
         ======================================================================
         Since the per-colour gallery (main-product.liquid, 2026-08-21) the
         page only carries the SELECTED colour's photographs, so the strip
         loaded for Black holds no thumbnail for White. product-info.js already
         swapped the main viewer's list to the new colour on a variant change;
         nothing swapped the strip. `setActiveMedia` then looked up White's
         thumbnail, got null, and threw on `.parentElement`.

         That throw is what customers felt, and it was not in the gallery. It
         unwound out of `updateMedia` and aborted the rest of
         `handleUpdateProductInfo` — the PRICE update and the re-enabling of
         the buy button, both of which run after the media step. Measured on
         live, 2026-09-29, `mono-sherry25-pro-large-handbag-black` at 1440:
         pick White, and the photo changes, the URL changes, the price stays
         at Black's Rs. 2,799 (White is Rs. 3,199) and ADD TO CART and BUY IT
         NOW stay disabled indefinitely. Every colour change, every product
         with a thumbnail strip.

         The WHOLE slider-component is replaced, not its list items: its
         classes are computed from the photograph count (`--no-slide` at 3 or
         fewer, which arrows show), and a colour with a single photograph gets
         no strip at all from the server. Swapping the list would leave all of
         that describing the previous colour. The server's rendering is the
         authority, exactly as it is for the viewer. */
      replaceThumbnails(next) {
        const current = this.elements.thumbnails;
        if (current === next) return;
        if (current && next) current.replaceWith(next);
        else if (current) current.remove();
        else if (next) this.elements.viewer.after(next);

        this.elements.thumbnails = next || null;
        if (!next) return;
        if (!this.slideListenerBound) {
          /* The strip arrives after construction on a product whose first
             colour had a single photograph; the viewer listener was skipped
             then by the constructor's early return. Bound once, ever — the
             viewer is never replaced, so a second listener would double-fire. */
          this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
          this.slideListenerBound = true;
        }
        this.bindThumbnails(next);
      }

      onSlideChanged(event) {
        if (!this.elements.thumbnails) return;
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`,
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        /* CROSS-FADE THE SWAP — docs/53 §3a B, "smooth the transitions".
           The animation is carried by a class this method adds, and NOT by a
           rule on `.is-active`, for one measured reason: `is-active` is
           authored by Liquid on the featured media, so an animation keyed to
           it would run at first paint — and an element at `opacity: 0` is not
           an LCP candidate in Chrome. The PDP's main image IS that candidate,
           so fading it in on load would push product LCP out by the whole
           duration. docs/54 §1.-10 spent a step recovering LCP on this theme;
           it is not worth 200ms of prettiness.

           `setActiveMedia` is only ever reached from a thumbnail click or from
           product-info.js's `updateMedia` on a variant change, never during
           initial render, so this hook cannot fire before first paint.

           The reflow read is what makes it replay: re-adding a class in the
           same frame it was removed does not restart a CSS animation. */
        if (activeMedia) {
          activeMedia.classList.remove('product__media-item--enter');
          void activeMedia.offsetWidth;
          activeMedia.classList.add('product__media-item--enter');
          activeMedia.addEventListener(
            'animationend',
            () => activeMedia.classList.remove('product__media-item--enter'),
            { once: true },
          );
        }

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          /* Guarded: a thumbnail can legitimately be absent (a colour whose
             strip the server did not render). See `replaceThumbnails` for what
             this one unguarded dereference cost. */
          const thumbnailToFront = this.elements.thumbnails?.querySelector(`[data-target="${mediaId}"]`);
          if (thumbnailToFront && thumbnailToFront.parentElement.firstChild !== thumbnailToFront) {
            thumbnailToFront.parentElement.prepend(thumbnailToFront);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }

          /* CHOOSING A COLOUR MUST NOT YANK THE PAGE BACK TO THE GALLERY —
             merchant instruction, 2026-08-14: "choosing a colour shouldnt
             scroll up to the picture since it makes it difficult for the
             shopper to shop".

             `prepend` is the tell, and it is already load-bearing: it is
             `false` from a thumbnail click (`bindThumbnails`) and `true`
             from product-info.js's `updateMedia` on a variant change. Those
             two want opposite things from the same method.

             Clicking a thumbnail is a request to LOOK at that image, so
             following it up the page is right. Picking a colour is a request
             to BUY that colour — the customer is at the picker, or at the
             sticky bar, several screens below the gallery, and Dawn's rule
             ("scroll if the image is above the fold") is guaranteed to fire
             there and throw them back to the top on every swatch.

             The horizontal `parentElement.scrollTo` above still runs in both
             cases: the carousel has to follow the variant, and moving it moves
             nothing the customer is looking at. */
          if (prepend) return;

          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        if (!activeThumbnail) return;
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    },
  );
}
